import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function CampaignsManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    icon: '',
    color: '#FF1493',
    title: '',
    brief: '',
    details: '',
    helpline: '',
    order: 1,
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const q = query(collection(db, 'campaigns'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const campaignsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      alert('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCampaign) {
        await updateDoc(doc(db, 'campaigns', editingCampaign.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Campaign updated successfully!');
      } else {
        await addDoc(collection(db, 'campaigns'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Campaign added successfully!');
      }
      setShowModal(false);
      setEditingCampaign(null);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      icon: campaign.icon,
      color: campaign.color,
      title: campaign.title,
      brief: campaign.brief,
      details: campaign.details,
      helpline: campaign.helpline,
      order: campaign.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await deleteDoc(doc(db, 'campaigns', id));
      alert('Campaign deleted successfully!');
      loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      icon: '',
      color: '#FF1493',
      title: '',
      brief: '',
      details: '',
      helpline: '',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Campaigns Management</h2>
            <button
              onClick={() => {
                setEditingCampaign(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Campaign
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && campaigns.length === 0 && (
            <p>No campaigns found. Add your first campaign!</p>
          )}

          {!loading && campaigns.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Title</th>
                  <th>Brief</th>
                  <th>Helpline</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>{campaign.order}</td>
                    <td>{campaign.title}</td>
                    <td>{campaign.brief}</td>
                    <td>{campaign.helpline}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(campaign.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Icon Name *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., warning, shield-checkmark"
                  required
                />
              </div>

              <div className="form-group">
                <label>Color *</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter campaign title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Brief Description *</label>
                <input
                  type="text"
                  value={formData.brief}
                  onChange={(e) => setFormData({ ...formData, brief: e.target.value })}
                  placeholder="Enter brief description"
                  required
                />
              </div>

              <div className="form-group">
                <label>Detailed Information *</label>
                <textarea
                  value={formData.details}
                  onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                  placeholder="Enter detailed information"
                  required
                  rows="5"
                />
              </div>

              <div className="form-group">
                <label>Helpline Number *</label>
                <input
                  type="text"
                  value={formData.helpline}
                  onChange={(e) => setFormData({ ...formData, helpline: e.target.value })}
                  placeholder="e.g., 181 or 1800-180-6410"
                  required
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Add Campaign'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCampaign(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignsManager;
